import {
    addMainControls,
    addOverlayControls,
    WithMainControlsProps,
    WithOverlayControlsProps,
} from '@eb/eds-structure';
import { sdkRequest } from '@eb/http';
import { setLanguage } from '@eb/i18n';
import { addDimensions, trackAnalyticsEvent } from '@eb/site-analytics';
import React, { useEffect, useState } from 'react';
import ContactOrganizerRoot from './components/ContactOrganizer/ContactOrganizerRoot';
import { EventCollections } from './components/EventCollections';
import FollowOrganizerRoot from './components/FollowOrganizer/FollowOrganizerRoot';
import NotificationRoot from './components/Notification/NotificationRoot';
import OverlayRoot from './components/Overlay/OverlayRoot';
import {
    CollectionResponse,
    EventCollectionsResponse,
} from './components/RelatedEvents/api';
import RelatedEventsRoot from './components/RelatedEvents/RelatedEventsRoot';
import { SearchTakeoverRoot } from './components/SearchTakeover/SearchTakeoverRoot';
import ShareEventRoot from './components/ShareEvent/ShareEventRoot';
import { AppProps, ControlledProps, EnhancedAppProps } from './types';

const setUpDimensions = (props: AppProps): void => {
    const {
        app_name: appName,
        app_version: appVersion,
        event,
        gaSettings,
    } = props;
    const {
        activeScopeUserId,
        correlationId,
        guestPartnerId,
        userPartnerId: userId,
        guestPartnerId: guestId,
    } = gaSettings;
    const {
        id: eventId,
        ownerId: eventOwnerUserId,
        source: eventSource,
    } = event;

    const dimensions = {
        activeScopeUserId,
        appName,
        appVersion,
        correlationId,
        eventId,
        eventOwnerUserId,
        eventSource,
        experimentId: 'OldListing',
        guestId,
        guestPartnerId,
        userId,
    };

    addDimensions(dimensions);
};

const ControlledListingComponents = (
    props: ControlledProps & WithMainControlsProps & WithOverlayControlsProps,
) => {
    const { creatorCollections } = props;

    const hasCreatorCollections =
        creatorCollections && creatorCollections.length > 0;

    return (
        <>
            <NotificationRoot {...props} />
            <RelatedEventsRoot {...props} />
            {hasCreatorCollections && (
                <EventCollections eventId={props.event.id} />
            )}
            <OverlayRoot {...props} />
        </>
    );
};

const EnhancedListingComponents = addOverlayControls<EnhancedAppProps>(
    addMainControls(ControlledListingComponents),
);

export const SettingsContext = React.createContext<any>({});

const useCreatorCollections = (
    eventId: string,
    launchLinkToCreatorCollectionsOnListing: boolean,
) => {
    const [collections, setCollections] = useState<
        CollectionResponse[] | undefined
    >(undefined);

    const isExpiredEvent = document.getElementsByClassName(
        'js-expired-related-events',
    );

    useEffect(() => {
        let isMounted = true;

        if (isExpiredEvent.length > 0) {
            return;
        }

        if (launchLinkToCreatorCollectionsOnListing) {
            sdkRequest(`/events/${eventId}/collections`).then(
                (storedCollections: EventCollectionsResponse) => {
                    if (storedCollections && isMounted)
                        return setCollections(storedCollections.collections);
                },
            );
        } else {
            return setCollections([]);
        }
        return () => {
            isMounted = false;
        };
    }, [eventId, launchLinkToCreatorCollectionsOnListing, isExpiredEvent]);

    return collections;
};

export const App: React.FC<AppProps> = (props: AppProps) => {
    const {
        env: {
            localeInfo: { locale },
        },
        event: { id: eventId },
        settings: {
            featureFlags: {
                launchLinkToCreatorCollectionsOnListing = false,
            } = {},
        },
    } = props;

    setLanguage(locale);

    setUpDimensions(props);
    trackAnalyticsEvent({
        action: 'AppLoad',
        category: 'listing',
    });
    const modalContainer = React.useRef<HTMLDivElement | null>(null);
    const creatorCollections = useCreatorCollections(
        eventId,
        launchLinkToCreatorCollectionsOnListing,
    );

    return (
        <div data-spec="listing-page-app">
            <SettingsContext.Provider value={props.settings}>
                <ShareEventRoot {...props} modalContainer={modalContainer} />
                <FollowOrganizerRoot {...props} />
                <ContactOrganizerRoot {...props} />
                <SearchTakeoverRoot {...props} />
                <EnhancedListingComponents
                    {...props}
                    creatorCollections={creatorCollections}
                />
                <div id="share-modal-wrapper" ref={modalContainer}></div>
            </SettingsContext.Provider>
        </div>
    );
};
